import type { ReactNode } from 'react';
import { Badge } from '@react-apps/design-system';
import type { BadgeProps } from '@react-apps/design-system';

/**
 * Um problema que o admin precisa resolver, com a ação de resolver dentro.
 *
 * Usado nos **dois** momentos da importação — pendência de pré-flight (antes de
 * subir qualquer coisa) e falha de fila (depois) — de propósito: é o mesmo tipo
 * de decisão para quem está olhando, e ler duas gramáticas visuais diferentes
 * para "essa imagem não serve" só atrasa o conserto.
 *
 * A cor não é decoração: `error` = a ação é **corrigir** (reenviar não muda
 * nada); `warning` = a fila ainda está tentando sozinha, o admin pode só esperar.
 */
export function ImportIssueCard({
  title,
  badge,
  badgeVariant = 'error',
  message,
  detail,
  actions,
}: {
  title: string;
  badge: string;
  badgeVariant?: BadgeProps['variant'];
  message: ReactNode;
  /** Segunda linha: contagem de tentativa, countdown, dica de correção. */
  detail?: ReactNode;
  actions?: ReactNode;
}) {
  const tone =
    badgeVariant === 'warning'
      ? 'border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/5'
      : 'border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/5';

  return (
    <div className={`space-y-2 rounded-lg border p-3 ${tone}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-(--color-text-primary) text-sm font-semibold">
          {title}
        </span>
        <Badge variant={badgeVariant}>{badge}</Badge>
      </div>
      <p className="text-(--color-text-primary) text-sm">{message}</p>
      {detail && (
        <p className="text-(--color-text-secondary) text-xs">{detail}</p>
      )}
      {actions && <div className="flex flex-wrap gap-2 pt-1">{actions}</div>}
    </div>
  );
}
